<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>

        <div v-else>
        <vue-headful :title="'Safety Meeting on '+ this.$options.filters.moment(meeting.meeting_date, 'MM/DD/YYYY') + ' :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/safety-meetings`">
                            <i class="fa fa-life-ring"></i> Safety Meetings
                        </router-link>
                    </li>
                    <li class="breadcrumb-item active">
                        Meeting on {{ meeting.meeting_date | moment("MM/DD/YYYY") }}
                    </li>
                </ol>
            </h4>

            <div class="row mb-4">
                <div class="col-xl-8 col-lg-12">
                    <div class="card mb-3">
                        <div class="card-header">
                            <strong class="d-sm-inline d-block">Safety Meeting</strong>

                            <div class="float-sm-right mt-sm-0 mt-3" v-if="canEdit">
                                <router-link :to="`/safety-meetings/edit/${meeting.safety_meeting_id}`" class="btn btn-sm btn-light">
                                    <i class="fa fa-edit"></i> Edit
                                </router-link>

                                <b-button variant="light" size="sm" v-on:click="cloneSafetyMeeting()"><i class="fa fa-copy"></i> Clone</b-button>

                                <b-dropdown right text="Distribute" variant="light" size="sm">
                                    <b-dropdown-item :to="`/safety-meetings/email/${meeting.safety_meeting_id}`"><i class="fa fa-envelope"></i> Email</b-dropdown-item>
                                    <b-dropdown-item :to="`/safety-meetings/text/${meeting.safety_meeting_id}`"><i class="fa fa-comment"></i> Text</b-dropdown-item>
                                </b-dropdown>

                                <router-link :to="`/safety-meetings/delete/${meeting.safety_meeting_id}`" class="btn btn-sm btn-light">
                                    <span class="text-danger"><i class="fa fa-trash"></i> Delete</span>
                                </router-link>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Meeting Status:</div>
                                <div class="col-md-9">
                                    <label class="badge badge-success" v-if="meeting.meeting_status == 'Closed'">Closed</label>
                                    <label class="badge badge-danger" v-else>Open</label>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Meeting Title:</div>
                                <div class="col-md-9">{{ meeting.meeting_title }}</div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Meeting Date:</div>
                                <div class="col-md-9">{{ meeting.meeting_date | moment("MM/DD/YYYY") }}</div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Created By:</div>
                                <div class="col-md-9"><user-name :user="meeting" /></div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Jobsite:</div>
                                <div class="col-md-9">
                                    <router-link :to="`/jobsites/view/${meeting.JobsiteId}`">
                                        {{ meeting.JobsiteNumber }} - {{ meeting.JobsiteName }}
                                    </router-link>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3" v-if="meeting.meeting_key">
                                <div class="col col text-muted">Public URL:</div>
                                <div class="col-md-9">
                                    <a :href="`/safety-meetings/key/${meeting.meeting_key}`" target="_blank" v-if="meeting.meeting_key">
                                        {{ $root.appUrl }}/safety-meetings/key/{{ meeting.meeting_key }}
                                    </a>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3" v-if="meeting.meeting_key">
                                <div class="col col text-muted">
                                    Automatically Add Employees As Attendees:
                                </div>
                                <div class="col-md-9">
                                    <span class="badge badge-success" v-if="meeting.auto_add_attendees">Yes</span>
                                    <span class="badge badge-warning" v-else>No</span>
                                    <i class="fa fa-question-circle" title="If set to 'Yes', MeritEHS will automatically add employees as meeting attendees when they click the Public URL."></i>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3" v-if="meeting.subcontractor_id">
                                <div class="col text-muted">Subcontractor:</div>
                                <div class="col-md-9">
                                    <router-link :to="`/subcontractors/view/${meeting.subcontractor_id}`">
                                        {{ meeting.subcontractor_name }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-xl-4 col-lg-12">
                    <div class="card" v-if="meeting.meeting_key">
                        <div class="card-header">
                            QR Code <i class="fa fa-question-circle" title="Use this to create a scannable barcode that links back to this meeting. This can be printed and displayed where easily accessible."></i>
                        </div>
                        <div class="card-body" style="overflow-x: auto;">
                            <div class="row">
                                <div class="col-xl-12 col-sm-6 col-xs-12">
                                    <b-input-group class="mb-2">
                                        <template #prepend>
                                            <b-input-group-text class="bg-light">Size</b-input-group-text>
                                        </template>
                                        <b-form-select v-model="qr_code_size" :options="qrCodeOptions"></b-form-select>
                                    </b-input-group>

                                    <span v-show="qr_loading"><i class="fa fa-spin fa-spinner"></i></span>

                                    <div v-show="!qr_loading">
                                        <img :src="`data:image/png;base64,${qr_code_html}`" />
                                    </div>

                                    <div class="mt-2">
                                        <a :href="downloadQrCode" target="_blank">Download QR Code</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ul class="mt-4 search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
                <template v-for="(tab, key) in settingsTabs">
                    <b-nav-item :active="curTab === key" @click="curTab = key">
                        {{ tabTitle(key) }}
                    </b-nav-item>
                </template>
            </ul>

            <div class="mt-4 mb-2 mb-lg-0 d-lg-none d-block">
              <b-dropdown :html="tabButtonTitle" variant="default" class="bg-white">
                <template v-for="(tab, key) in settingsTabs">
                    <b-dropdown-item :active="curTab === key" @click="curTab = key">
                        {{ tabTitle(key) }}
                    </b-dropdown-item>
                </template>
              </b-dropdown>
            </div>

            <div class="mt-2">
                <div class="card" v-show="curTab === 'notes'">
                    <div class="card-body">
                        <div v-html="meeting.meeting_notes"></div>
                    </div>
                </div>

                <div class="card" v-show="curTab === 'files'">
                    <div class="card-header">
                        <strong>Files &amp; Photos</strong>
                    </div>
                    <div class="card-body">
                        <template v-if="files.length > 0">
                            <file-list :files="files" boxSize="col-md-4 col-12" />
                        </template>
                        <template v-else>
                            -- No Files --
                        </template>
                    </div>
                </div>

                <div class="card" v-show="curTab === 'attendees'">
                    <div class="card-body">
                        <v-server-table ref="meetingAttendeeTbl" :url="`/safety-meetings/employees/${meeting.safety_meeting_id}/attendees`" :columns="attendeeColumns" :options="attendeeOptions">
                            <div slot="afterFilter" class="ml-2">
                                <button type="button" class="btn btn-outline-success" @click="exportDataTable('attendees')" :disabled="exportingTable">
                                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                                </button>
                            </div>

                            <router-link slot="LastName" slot-scope="props" :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                                {{ props.row.FirstName }} {{ props.row.LastName }}
                            </router-link>

                            <template slot="read_at" slot-scope="props">
                                <span v-if="props.row.read_at">{{ props.row.read_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="completed_at" slot-scope="props">
                                <span v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                        </v-server-table>
                    </div>
                </div>
                <div class="card" v-show="curTab === 'read'">
                    <div class="card-body">
                        <v-server-table ref="meetingEmployeeTbl" :url="`/safety-meetings/employees/${meeting.safety_meeting_id}/public`" :columns="columns" :options="options">
                            <div slot="afterFilter" class="ml-2">
                                <button type="button" class="btn btn-outline-success" @click="exportDataTable('public')" :disabled="exportingTable">
                                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                                </button>
                            </div>

                            <router-link slot="LastName" slot-scope="props" :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                                {{ props.row.FirstName }} {{ props.row.LastName }}
                            </router-link>

                            <template slot="read_at" slot-scope="props">
                                <span v-if="props.row.read_at">{{ props.row.read_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="completed_at" slot-scope="props">
                                <span v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="email_sent_at" slot-scope="props">
                                <span v-if="props.row.email_sent_at">{{ props.row.email_sent_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="text_sent_at" slot-scope="props">
                                <span v-if="props.row.text_sent_at">{{ props.row.text_sent_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="count_files_opened" slot-scope="props">
                                <a href="javascript:void(0)" v-on:click="getFilesOpened(props.row.id)" title="View Files">{{ props.row.count_files_opened }}</a>
                            </template>

                            <template slot="child_row" slot-scope="props">
                                <div v-if="!files_loading">
                                    <table class="table table-bordered mb-3">
                                        <thead>
                                            <tr>
                                                <th>Files Opened <span v-if="viewed_files[props.row.id]">({{ viewed_files[props.row.id].length }})</span></th>
                                                <th>Date Opened</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="file in viewed_files[props.row.id]">
                                                <td>
                                                    <a :href="`/files/view/${file.file_id}`">{{ file.file_name }}</a>
                                                </td>
                                                <td>
                                                    {{ file.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <a href="javascript:void(0)" v-on:click="$refs.meetingEmployeeTbl.toggleChildRow(props.row.id)" class="text-danger"><i class="fa fa-remove"></i> Close Viewed Files</a>
                                </div>
                                <div v-else>
                                    <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                                </div>
                            </template>

                        </v-server-table>
                    </div>
                </div>
                <div class="card" v-show="curTab === 'emails'">
                    <div class="card-body">
                        <v-server-table ref="meetingEmployeeTblEmail" :url="`/safety-meetings/employees/${meeting.safety_meeting_id}/email`" :columns="emailColumns" :options="emailOptions">
                            <div slot="afterFilter" class="ml-2">
                                <button type="button" class="btn btn-outline-success" @click="exportDataTable('email')" :disabled="exportingTable">
                                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                                </button>
                            </div>

                            <router-link slot="LastName" slot-scope="props" :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                                {{ props.row.FirstName }} {{ props.row.LastName }}
                            </router-link>

                            <template slot="email_sent_at" slot-scope="props">
                                <span>{{ props.row.email_sent_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="read_at" slot-scope="props">
                                <span v-if="props.row.read_at">{{ props.row.read_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="completed_at" slot-scope="props">
                                <span v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                        </v-server-table>
                    </div>
                </div>
                <div class="card" v-show="curTab === 'texts'">
                    <div class="card-body">
                        <v-server-table ref="meetingEmployeeTblText" :url="`/safety-meetings/employees/${meeting.safety_meeting_id}/text`" :columns="textColumns" :options="textOptions">
                            <div slot="afterFilter" class="ml-2">
                                <button type="button" class="btn btn-outline-success" @click="exportDataTable('text')" :disabled="exportingTable">
                                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                                </button>
                            </div>

                            <router-link slot="LastName" slot-scope="props" :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                                {{ props.row.FirstName }} {{ props.row.LastName }}
                            </router-link>

                            <template slot="text_sent_at" slot-scope="props">
                                <span>{{ props.row.text_sent_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="read_at" slot-scope="props">
                                <span v-if="props.row.read_at">{{ props.row.read_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                            <template slot="completed_at" slot-scope="props">
                                <span v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                            </template>

                        </v-server-table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                curTab: 'notes',
                loading: true,
                files_loading: false,
                exportingTable: false,
                totalAttendees: null,
                totalReadBy: null,
                totalEmailed: null,
                totalTexted: null,
                userTimezone: this.$store.state.user.timezone,
                safety_meeting_id: this.$route.params.id,
                meeting: {},
                files: [],
                viewed_files: {},

                qr_loading: false,
                qr_code_size: 5,
                qr_code_html: null,
                qrCodeOptions: [
                    {value: 3, text: '87x87'},
                    {value: 4, text: '116x116'},
                    {value: 5, text: '145x145'},
                    {value: 6, text: '174x174'},
                    {value: 7, text: '203x203'},
                    {value: 8, text: '232x232'},
                    {value: 9, text: '261x261'},
                ],

                attendeeColumns: ['LastName', 'position', 'EmployeeNumber', 'email', 'Phone'],
                attendeeOptions: {
                    headings: {
                        LastName: 'Name',
                        position: 'Position',
                        EmployeeNumber: 'Employee Number',
                        email: 'Email',
                        Phone: 'Phone',
                    },
                    orderBy: {
                        ascending: true,
                        column: 'employee.LastName',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.totalAttendees = response.data.count;

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                columns: ['LastName', 'position', 'EmployeeNumber', 'email', 'Phone', 'read_at', 'completed_at', 'email_sent_at', 'text_sent_at', 'count_emails_sent', 'count_texts_sent', 'count_files_opened'],
                options: {
                    showChildRowToggler: false,
                    headings: {
                        LastName: 'Name',
                        position: 'Position',
                        EmployeeNumber: 'Employee Number',
                        email: 'Email',
                        Phone: 'Phone',
                        read_at: '1st Time Notes Opened',
                        completed_at: '1st Time Notes Completed',
                        email_sent_at: '1st Time Emailed',
                        text_sent_at: '1st Time Texted',
                        count_emails_sent: '# Times Emailed',
                        count_texts_sent: '# Times Texted',
                        count_files_opened: '# Files Opened',
                    },
                    orderBy: {
                        ascending: true,
                        column: 'employee.LastName',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.totalReadBy = response.data.count;

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                emailColumns: ['LastName', 'position', 'EmployeeNumber', 'email', 'count_emails_sent', 'email_sent_at', 'read_at', 'completed_at'],
                emailOptions: {
                    headings: {
                        LastName: 'Name',
                        position: 'Position',
                        EmployeeNumber: 'Employee Number',
                        email: 'Email',
                        count_emails_sent: '# Times Emailed',
                        email_sent_at: '1st Time Email Sent',
                        read_at: '1st Time Notes Opened',
                        completed_at: '1st Time Notes Completed',
                    },
                    orderBy: {
                        ascending: true,
                        column: 'employee.LastName',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.totalEmailed = response.data.count;

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                textColumns: ['LastName', 'position', 'EmployeeNumber', 'email', 'count_texts_sent', 'text_sent_at', 'read_at', 'completed_at'],
                textOptions: {
                    headings: {
                        LastName: 'Name',
                        position: 'Position',
                        EmployeeNumber: 'Employee Number',
                        email: 'Email',
                        count_texts_sent: '# Times Texted',
                        text_sent_at: '1st Time Text Sent',
                        read_at: '1st Time Notes Opened',
                        completed_at: '1st Time Notes Completed',
                    },
                    orderBy: {
                        ascending: true,
                        column: 'employee.LastName',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.totalTexted = response.data.count;

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },
            }
        },

        watch: {
            qr_code_size(newVal, oldVal){
                //Get the QR Code
                this.getQrCode();
            },
        },

        computed: {

            settingsTabs(){
                return {
                    notes: 'Meeting Notes',
                    files: 'Files & Photos ('+this.files.length+')',
                    attendees: 'Attendees',
                    read: 'Employees Who Read the Notes',
                    emails: 'Email Blasts',
                    texts: 'Text Blasts',
                };
            },

            canEdit(){
                //Check for manager
                if(this.$root.hasPermission('safety.meetings.manage'))
                {
                    return true;
                }

                //Check create and user
                if(this.$root.hasPermission('safety.meetings.create') && this.meeting.CreatorId == this.$store.state.user.UserId)
                {
                    return true;
                }

                //Default Defense
                return false;
            },

            tabButtonTitle(){
                //Send it
                var html = '<i class="fa fa-bars"></i> ' + this.settingsTabs[this.curTab];

                //Switch
                switch(this.curTab)
                {
                    case 'attendees':
                        //Append Count
                        html += ' ('+this.totalAttendees+')';
                        break;

                    case 'read':
                        //Append Count
                        html += ' ('+this.totalReadBy+')';
                        break;

                    case 'emails':
                        //Append Count
                        html += ' ('+this.totalEmailed+')';
                        break;

                    case 'texts':
                        //Append Count
                        html += ' ('+this.totalTexted+')';
                        break;
                }

                //Send it
                return html;
            },

            downloadQrCode(){
                //SEtup
                return '/safety-meetings/qr-code/'+this.meeting.safety_meeting_id+'?download=true&qr_code_size='+this.qr_code_size;
            },
        },

        methods: {

            getSafetyMeeting: function() {
                //Get the data
                this.$http
                    .get('/safety-meetings/view/' + this.safety_meeting_id)
                    .then(response => {
                        //Set all the data
                        this.meeting = response.data.meeting;
                        this.files = response.data.files;

                        //Get the QR Code
                        if(this.meeting.meeting_key)
                        {
                            this.getQrCode();
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getQrCode(){
                //QR Code
                this.qr_loading = true;

                //Setup
                var payload = {
                    qr_code_size: this.qr_code_size,
                };

                //Get the data
                this.$http
                    .get('safety-meetings/qr-code/' + this.meeting.safety_meeting_id, payload)
                    .then(response => {
                        //Set all the data
                        this.qr_code_html = response.data.qr_code;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.qr_loading = false;
                    });
            },

            getFilesOpened(id){
                //Open the Row
                this.$refs.meetingEmployeeTbl.toggleChildRow(id);

                //IF the files are already set, stop
                if(this.viewed_files[id])
                {
                    return;
                }

                //Setup
                this.files_loading = true;

                //Get the data
                this.$http
                    .get('/safety-meetings/viewed-files/' + this.safety_meeting_id + '/' + id)
                    .then(response => {
                        //Now what?
                        this.viewed_files[id] = response.data;

                        //Force it?
                        this.$forceUpdate();
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Setup
                        this.files_loading = false;
                    });
            },

            exportDataTable(type){
                //Loading indicator
                this.exportingTable = true;

                //Setup Columns
                var raw_cols = JSON.parse(JSON.stringify(this.$refs.meetingEmployeeTbl.columns));

                //Build the List that is shown
                var cols = [];

                //Loop and Process
                for (var i = raw_cols.length - 1; i >= 0; i--)
                {
                    //Check if shown
                    if(document.querySelectorAll('input[value='+raw_cols[i]+']').length > 0 && document.querySelectorAll('input[value='+raw_cols[i]+']')[0].checked)
                    {
                        cols.push(raw_cols[i]);
                    }
                }

                //Build the payload
                var payload = {
                    //filters: this.extractFilters(),
                    query: (this.$refs.meetingEmployeeTbl.getRequestParams()).query,
                    orderBy: this.$refs.meetingEmployeeTbl.options.orderBy.column,
                    ascending: this.$refs.meetingEmployeeTbl.options.orderBy.ascending,
                    table: 'meetingEmployeeTbl',
                    columns: cols,
                    export: true,
                };

                //Send it
                this.$http.get('/safety-meetings/employees/' + this.meeting.safety_meeting_id + '/' + type, payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {
                        //Alert
                        this.$toastr.error(err.response.data, 'Failed to Export Safety Meeting Employees');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            cloneSafetyMeeting(){
                //Confirm it
                if(!confirm('Are you sure you want to clone this safety-meeting?'))
                {
                    return;
                }

                //Send it
                this.cloning = true;

                //console.log(this.meeting.safety_meeting_id);

                //Send it
                this.$http
                    .post('safety-meetings/clone/' + this.meeting.safety_meeting_id)
                    .then(response => {
                        //Return to main list?

                        this.$router.push('/safety-meetings/edit/' + response.data.safety_meeting_id);

                    })
                    .catch(response => {

                    })
                    .then(response => {
                        //Rest
                        this.cloning = false;
                    });
            },

            tabTitle(key){
                //Send it
                var html = this.settingsTabs[key];

                //Switch
                switch(key)
                {
                    case 'attendees':
                        //Append Count
                        if(this.totalAttendees)
                        {
                            //Append
                            html += ' ('+this.totalAttendees+')';
                        }
                        break;

                    case 'read':
                        //Append Count
                        if(this.totalReadBy)
                        {
                            //Append
                            html += ' ('+this.totalReadBy+')';
                        }
                        break;

                    case 'emails':
                        //Append Count
                        if(this.totalEmailed)
                        {
                            //Append
                            html += ' ('+this.totalEmailed+')';
                        }
                        break;

                    case 'texts':
                        //Append Count
                        if(this.totalTexted)
                        {
                            //Append
                            html += ' ('+this.totalTexted+')';
                        }
                        break;
                }

                //Send it
                return html;
            },
        },

        mounted() {
            //Get the Safety Meeting
            this.getSafetyMeeting();
        }
    }
</script>
